import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';

type Props = {
  open: boolean;
  languageLabel: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
};

// Rendered inside the Sheet (no Portal) so `absolute inset-0` centers within the
// sheet rather than the screen. See the "Dialogs Inside Sheets" pattern in
// CLAUDE.md and SheetConfirmationCloseDialog.
export const DeleteLanguageDialog = ({ open, languageLabel, onOpenChange, onConfirm }: Props) => {
  const { t } = useTranslation('website');

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 z-20 bg-black/30" />
      <DialogPrimitive.Content
        className="absolute inset-0 z-30 flex items-center justify-center p-4 focus:outline-none"
        role="alertdialog"
        aria-modal="true"
      >
        <div className="w-full max-w-md space-y-4 rounded-lg bg-card p-6 text-card-foreground shadow-lg">
          <DialogPrimitive.Title className="text-lg font-bold">
            {t('text_content_languages.delete_title')}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="text-sm text-muted-foreground">
            {t('text_content_languages.delete_description', { language: languageLabel })}
          </DialogPrimitive.Description>

          <Separator className="my-2" />

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="md"
              onClick={() => onOpenChange(false)}
              data-testid="delete-language-cancel"
            >
              {t('text_content_languages.delete_cancel')}
            </Button>
            <Button
              variant="danger"
              size="md"
              onClick={onConfirm}
              data-testid="delete-language-confirm"
            >
              {t('text_content_languages.delete_confirm')}
            </Button>
          </div>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};
